
import { useState } from 'react';

export default function Home() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const services = [
    {
      icon: "ri-home-4-line",
      title: "Housing Guidance",
      description: "Know your rights, understand banding, and manage temporary accommodation.",
      color: "blue"
    },
    {
      icon: "ri-money-pound-circle-line",
      title: "Universal Credit Support",
      description: "Help with applications, journals, and letters.",
      color: "green"
    },
    {
      icon: "ri-global-line",
      title: "Language Assistance",
      description: "Breaking down official letters into plain English.",
      color: "purple"
    },
    {
      icon: "ri-file-text-line",
      title: "Government Forms",
      description: "One-to-one support filling out applications.",
      color: "orange"
    },
    {
      icon: "ri-phone-line",
      title: "Advocacy",
      description: "Helping you communicate clearly with councils and services.",
      color: "teal"
    },
    {
      icon: "ri-parent-line",
      title: "Family Support",
      description: "Guidance with school places, childcare, and community resources.",
      color: "pink"
    }
  ];

  const colorClasses = {
    blue: { bg: "bg-blue-50", icon: "bg-blue-100 text-blue-600", border: "border-blue-200" },
    green: { bg: "bg-green-50", icon: "bg-green-100 text-green-600", border: "border-green-200" },
    purple: { bg: "bg-purple-50", icon: "bg-purple-100 text-purple-600", border: "border-purple-200" },
    orange: { bg: "bg-orange-50", icon: "bg-orange-100 text-orange-600", border: "border-orange-200" },
    teal: { bg: "bg-teal-50", icon: "bg-teal-100 text-teal-600", border: "border-teal-200" },
    pink: { bg: "bg-pink-50", icon: "bg-pink-100 text-pink-600", border: "border-pink-200" }
  };

  return (
    <>
      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-blue-500 to-blue-700 rounded-lg shadow-lg">
                  <i className="ri-community-line text-xl text-white"></i>
                </div>
                <span className="text-2xl font-bold text-blue-600" style={{ fontFamily: '"Inter", sans-serif' }}>
                  The Help Network London
                </span>
              </div>
            </div>
            <div className="hidden md:flex space-x-8">
              <a href="/" className="text-blue-600 font-medium cursor-pointer">Home</a>
              <a href="/services" className="text-gray-600 hover:text-blue-600 transition-colors cursor-pointer">Services</a>
              <a href="/about" className="text-gray-600 hover:text-blue-600 transition-colors cursor-pointer">About</a>
              <a href="/contact" className="text-gray-600 hover:text-blue-600 transition-colors cursor-pointer">Contact</a>
            </div>
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden w-6 h-6 flex items-center justify-center cursor-pointer"
            >
              <i className={`ri-${mobileMenuOpen ? 'close' : 'menu'}-line text-xl`}></i>
            </button>
          </div>
          
          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden border-t bg-white py-4">
              <div className="flex flex-col space-y-4 px-4">
                <a href="/" className="text-blue-600 font-medium cursor-pointer">Home</a>
                <a href="/services" className="text-gray-600 hover:text-blue-600 transition-colors cursor-pointer">Services</a>
                <a href="/about" className="text-gray-600 hover:text-blue-600 transition-colors cursor-pointer">About</a>
                <a href="/contact" className="text-gray-600 hover:text-blue-600 transition-colors cursor-pointer">Contact</a>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section 
        className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 py-20 lg:py-32 overflow-hidden"
        style={{
          backgroundImage: `url('https://public.readdy.ai/ai/img_res/e2bd56a8b82a6862b9f2f0df7ae151d7.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 to-blue-800/70"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Helping Communities Navigate Housing, Benefits & Government Services with
              <span className="text-blue-200"> Confidence</span>
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 mb-8 leading-relaxed max-w-3xl">
              At The Help Network London, we believe no one should feel lost when dealing with government systems. From housing applications to Universal Credit claims, we make the process clearer, less stressful, and more manageable.
            </p>
            <div className="bg-blue-800/30 backdrop-blur-sm p-6 rounded-lg border border-blue-300/20 mb-8">
              <p className="text-lg text-blue-100 leading-relaxed">
                <strong className="text-white">We are not legal advisors.</strong> Instead, we provide practical guidance, advice, and advocacy, empowering our community to move forward with confidence.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="/contact" className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-50 transition-colors cursor-pointer whitespace-nowrap shadow-lg">
                Get Support Today
              </a>
              <a href="/about" className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-blue-600 transition-colors cursor-pointer whitespace-nowrap">
                Learn More
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Key Highlights Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              How We Help Our Community
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We provide clear, practical support across all areas of government services, breaking down barriers and building confidence.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div 
                key={index} 
                className={`${colorClasses[service.color].bg} border ${colorClasses[service.color].border} p-6 rounded-lg hover:shadow-lg transition-shadow cursor-pointer`}
              >
                <div className={`w-12 h-12 flex items-center justify-center ${colorClasses[service.color].icon} rounded-lg mb-4`}>
                  <i className={`${service.icon} text-2xl`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-gray-700">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Services Overview */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Services</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              At The Help Network London, we offer clear, step-by-step guidance across a wide range of government and community issues.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Housing Guidance */}
            <div className="bg-white p-8 rounded-lg shadow-sm border">
              <div className="flex items-start mb-6">
                <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-lg mr-4 flex-shrink-0">
                  <i className="ri-home-4-line text-2xl text-blue-600"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Housing Guidance</h3>
                </div>
              </div>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <span>Support with council housing applications</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <span>Help understanding temporary accommodation</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <span>Guidance on banding, priority, and tenancy basics</span>
                </li>
              </ul>
            </div>

            {/* Universal Credit & Benefits */}
            <div className="bg-white p-8 rounded-lg shadow-sm border">
              <div className="flex items-start mb-6">
                <div className="w-12 h-12 flex items-center justify-center bg-green-100 rounded-lg mr-4 flex-shrink-0">
                  <i className="ri-money-pound-circle-line text-2xl text-green-600"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Universal Credit & Benefits</h3>
                </div>
              </div>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-green-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <span>Step-by-step UC applications</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-green-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <span>PIP (Personal Independence Payment) applications</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-green-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <span>Carer's Allowance form assistance</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-green-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <span>Support with reporting changes and understanding letters</span>
                </li>
              </ul>
            </div>

            {/* Language Support */}
            <div className="bg-white p-8 rounded-lg shadow-sm border">
              <div className="flex items-start mb-6">
                <div className="w-12 h-12 flex items-center justify-center bg-purple-100 rounded-lg mr-4 flex-shrink-0">
                  <i className="ri-global-line text-2xl text-purple-600"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Language Support</h3>
                </div>
              </div>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <span>Breaking down official letters in simple English</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <span>Helping with form-filling</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <span>Signposting to translation and ESOL classes</span>
                </li>
              </ul>
            </div>

            {/* Government Forms */}
            <div className="bg-white p-8 rounded-lg shadow-sm border">
              <div className="flex items-start mb-6">
                <div className="w-12 h-12 flex items-center justify-center bg-orange-100 rounded-lg mr-4 flex-shrink-0">
                  <i className="ri-file-text-line text-2xl text-orange-600"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Government Forms</h3>
                </div>
              </div>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-orange-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <span>Passport application assistance</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-orange-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <span>Driver's licence applications and renewals</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-orange-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <span>DVLA form assistance</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-orange-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <span>SIA (Security Industry Authority) applications</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Additional Services */}
          <div className="grid md:grid-cols-2 gap-8 mt-8">
            {/* Advocacy & Communication */}
            <div className="bg-white p-8 rounded-lg shadow-sm border">
              <div className="flex items-start mb-6">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-lg mr-4 flex-shrink-0">
                  <i className="ri-phone-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Advocacy & Communication</h3>
                </div>
              </div>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-teal-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <span>Help drafting letters and emails</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-teal-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <span>Preparing for appointments with councils and services</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-teal-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <span>Supporting you through difficult conversations</span>
                </li>
              </ul>
            </div>

            {/* Family Support & Training */}
            <div className="bg-white p-8 rounded-lg shadow-sm border">
              <div className="flex items-start mb-6">
                <div className="w-12 h-12 flex items-center justify-center bg-pink-100 rounded-lg mr-4 flex-shrink-0">
                  <i className="ri-parent-line text-2xl text-pink-600"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Family Support & Training</h3>
                </div>
              </div>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-pink-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <span>Guidance with school places and applications</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-pink-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <span>Theory test teaching and preparation</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-pink-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <span>Childcare support and funding options</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-pink-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <span>Community resources and local support networks</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="text-center mt-12">
            <a href="/services" className="inline-flex items-center bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap">
              View All Services
              <div className="w-5 h-5 flex items-center justify-center ml-2">
                <i className="ri-arrow-right-line"></i>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Why Choose The Help Network London?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We understand the challenges facing communities and individuals. Our approach is practical, supportive, and focused on your success.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-blue-100 rounded-full mx-auto mb-6">
                <i className="ri-heart-line text-3xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Community Focused</h3>
              <p className="text-gray-600">We understand community challenges and work specifically with local systems and services.</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-6">
                <i className="ri-user-smile-line text-3xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Personal Approach</h3>
              <p className="text-gray-600">Every situation is different. We provide tailored guidance that fits your specific circumstances.</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-purple-100 rounded-full mx-auto mb-6">
                <i className="ri-shield-check-line text-3xl text-purple-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Independent &amp; Trustworthy</h3>
              <p className="text-gray-600">We work for you, not government departments. Our advice is impartial and focused on your best interests.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-indigo-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-6">
            <i className="ri-arrow-right-circle-line text-3xl text-white"></i>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to get support?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Contact us today and take the stress out of dealing with government services. We're here to help you navigate the system with confidence.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/contact" className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-50 transition-colors cursor-pointer whitespace-nowrap shadow-lg">
              Contact Us Today
            </a>
            <a href="tel:+442089607792" className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-blue-600 transition-colors cursor-pointer whitespace-nowrap">
              Call: +44 20 8960 7792
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-8 h-8 flex items-center justify-center bg-gradient-to-br from-blue-400 to-blue-600 rounded-lg">
                  <i className="ri-community-line text-lg text-white"></i>
                </div>
                <span className="text-2xl font-bold text-blue-400" style={{ fontFamily: '"Inter", sans-serif' }}>
                  The Help Network London
                </span>
              </div>
              <p className="mt-4 text-gray-400">
                Independent guidance for government services across communities
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Services</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="/services" className="hover:text-white transition-colors cursor-pointer">Housing Guidance</a></li>
                <li><a href="/services" className="hover:text-white transition-colors cursor-pointer">Universal Credit Support</a></li>
                <li><a href="/services" className="hover:text-white transition-colors cursor-pointer">Benefits Advice</a></li>
                <li><a href="/services" className="hover:text-white transition-colors cursor-pointer">Form Assistance</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Support</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="/contact" className="hover:text-white transition-colors cursor-pointer">Contact Us</a></li>
                <li><a href="/about" className="hover:text-white transition-colors cursor-pointer">About Us</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">FAQs</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">Resources</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">Terms of Service</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">Accessibility</a></li>
                <li><a href="https://readdy.ai/?origin=logo" className="hover:text-white transition-colors cursor-pointer">Made with Readdy</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-400">
            <p>&copy; 2024 The Help Network London. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </>
  );
}
